import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import connieSuitcase from '../../../assets/connie-suitcase-drinking-no-bckgn.png';
import { MONTH_NAMES } from '../../../utils/dateUtils';
import type { FormData } from '../../../types';
import { CtaButton } from './shared';

interface Props { form: FormData; onChange: (f: keyof FormData, v: unknown) => void; onNext: () => void; onBack?: () => void; }

function toDateStr(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function StepDates({ form, onChange, onNext, onBack }: Props) {
  const mobile = useIsMobile();
  const f = mobile ? theme.form.mobile : theme.form;
  const [currentMonth, setCurrentMonth] = useState(() =>
    form.startDate ? new Date(form.startDate + 'T00:00:00') : new Date()
  );
  const [activeField, setActiveField] = useState<'start' | 'end'>('start');
  const startDate = form.startDate;
  const endDate = form.endDate;

  const MAX_DAYS = 14;

  const duration = startDate && endDate
    ? Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1
    : null;
  const valid = startDate && endDate && duration !== null && duration > 0 && duration <= MAX_DAYS;

  const clampEndDate = (start: string, end: string): string => {
    const diff = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000) + 1;
    if (diff <= MAX_DAYS) return end;
    const clamped = new Date(new Date(start).getTime() + (MAX_DAYS - 1) * 86400000);
    return toDateStr(clamped);
  };

  const handleDateClick = (day: number) => {
    const clicked = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const ds = toDateStr(clicked);
    if (ds < todayStr) return;
    if (activeField === 'start' || !startDate) {
      onChange('startDate', ds);
      if (endDate && endDate <= ds) onChange('endDate', '');
      setActiveField('end');
    } else {
      if (ds <= startDate) {
        onChange('startDate', ds);
        onChange('endDate', '');
      } else {
        onChange('endDate', clampEndDate(startDate, ds));
        setActiveField('start');
      }
    }
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const todayStr = toDateStr(new Date());

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= totalDays; d++) days.push(d);

  const handleStartInput = (v: string) => {
    if (!v) { onChange('startDate', ''); return; }
    if (v < todayStr) return;
    onChange('startDate', v);
    if (endDate && endDate <= v) onChange('endDate', '');
  };

  const handleEndInput = (v: string) => {
    if (!v) { onChange('endDate', ''); return; }
    if (startDate && v <= startDate) return;
    onChange('endDate', startDate ? clampEndDate(startDate, v) : v);
  };

  return (
    <div style={{ maxWidth: mobile ? '100%' : 'min(70%, 480px)', margin: '0 auto' }}>
      {/* Connie speech heading */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md, marginBottom: mobile ? theme.spacing['2xl'] : theme.spacing['4xl'] }}>
        <img src={connieSuitcase} alt="Connie" referrerPolicy="no-referrer"
          style={{ width: 'auto', height: mobile ? 'min(10vh, 80px)' : 'min(15vh, 150px)', objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <div>
          <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: f.headingSize, fontWeight: theme.fontWeight.light, color: theme.colors.navy, margin: 0, letterSpacing: '-0.02em', textAlign: 'center' }}>
            "When shall we{' '}
            <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>depart?</span>"
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: f.subtitleSize, fontWeight: theme.fontWeight.light, margin: `${theme.spacing.xs} 0 0`, textAlign: 'center' }}>
            Exact dates help me find the perfect flights and stays for you.
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div style={{ background: 'white', borderRadius: theme.borderRadius['2xl'], padding: theme.spacing['2xl'], border: `1px solid ${theme.colors.borderLight}`, boxShadow: theme.shadows.card }}>
        {/* Month nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg }}>
          <h3 style={{ fontFamily: theme.fontFamily.display, fontSize: theme.fontSize.lg, fontWeight: theme.fontWeight.bold, color: theme.colors.navy, margin: 0 }}>
            {MONTH_NAMES[month]} {year}
          </h3>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            {[
              { icon: <ArrowLeft size={16} />, onClick: () => setCurrentMonth(new Date(year, month - 1)) },
              { icon: <ArrowRight size={16} />, onClick: () => setCurrentMonth(new Date(year, month + 1)) },
            ].map(({ icon, onClick }, i) => (
              <button key={i} onClick={onClick} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: theme.colors.borderLight, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textMuted }}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: theme.spacing.sm }}>
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: theme.fontSize.xs, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, padding: `${theme.spacing.xs} 0` }}>{d}</div>
          ))}
        </div>

        {/* Days grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, justifyItems: 'center' }}>
          {days.map((day, idx) => {
            if (!day) return <div key={idx} />;
            const ds = toDateStr(new Date(year, month, day));
            const isStart = ds === startDate;
            const isEnd = ds === endDate;
            const inRange = !!(startDate && endDate && ds > startDate && ds < endDate);
            const isToday = ds === todayStr;

            const isPast = ds < todayStr;
            const isDisabled = isPast || (!!(startDate && !endDate) && ds === startDate);
            return (
              <button key={idx} onClick={() => handleDateClick(day)} disabled={isDisabled} style={{
                width: mobile ? 32 : 40, height: mobile ? 32 : 40, borderRadius: '50%', border: 'none',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                fontSize: theme.fontSize.sm, fontWeight: isStart || isEnd ? theme.fontWeight.bold : theme.fontWeight.normal,
                background: isStart || isEnd ? theme.colors.teal : inRange ? theme.colors.tealBg : 'transparent',
                color: isDisabled ? theme.colors.gray300 : isStart || isEnd ? 'white' : isToday ? theme.colors.teal : theme.colors.navy,
                outline: isToday && !isStart && !isEnd ? `1px solid ${theme.colors.teal}` : 'none',
                opacity: isPast ? 0.35 : 1,
                transition: 'background 0.15s',
              }}>
                {day}
              </button>
            );
          })}
        </div>

        {/* Date summary — clickable field selector */}
        <div style={{ display: 'flex', gap: theme.spacing.md, paddingTop: theme.spacing.lg, marginTop: theme.spacing.lg, borderTop: `1px solid ${theme.colors.borderLight}` }}>
          {(['start', 'end'] as const).map(field => {
            const isActive = activeField === field;
            const label = field === 'start' ? 'Departure' : 'Return';
            const value = field === 'start' ? startDate : endDate;
            return (
              <button key={field} type="button" onClick={() => setActiveField(field)} style={{
                flex: 1, textAlign: field === 'start' ? 'left' : 'right',
                background: isActive ? theme.colors.tealBg : 'transparent',
                border: `2px solid ${isActive ? theme.colors.teal : 'transparent'}`,
                borderRadius: theme.borderRadius.md,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                cursor: 'pointer', transition: 'all 0.15s',
              }}>
                <div style={{ fontSize: theme.fontSize.xs, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: isActive ? theme.colors.teal : theme.colors.muted, marginBottom: 2 }}>
                  {isActive ? '▸ ' : ''}{label}
                </div>
                <div style={{ fontFamily: theme.fontFamily.display, fontSize: theme.fontSize.base, fontWeight: theme.fontWeight.bold, color: value ? theme.colors.navy : theme.colors.muted }}>
                  {value || 'Select date'}
                </div>
                {isActive && (
                  <input type="date" value={value} min={field === 'end' ? (startDate || todayStr) : todayStr}
                    onChange={e => field === 'start' ? handleStartInput(e.target.value) : handleEndInput(e.target.value)}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <CtaButton label={duration ? `${duration} days — sounds good` : 'Continue'} onClick={onNext} disabled={!valid} onBack={onBack} />
    </div>
  );
}
